"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DESIGN_PREVIEW_CREDITS_NOTE } from "@/constants/serviceFacts";
import TrackedSection from "./TrackedSection";

const PROCESS_STEPS = [
  {
    step: "01",
    phase: "Design",
    title: "Create a Dress",
    description: "Describe the style, occasion, fabric, and fit you want.",
    detail: "AI turns your ideas into a design you can review and refine.",
    time: "1–2 minutes",
  },
  {
    step: "02",
    phase: "Estimate",
    title: "Review Your Estimate",
    description: "Create a front preview, then choose Get free quote.",
    detail: `${DESIGN_PREVIEW_CREDITS_NOTE} Sign in or create an account when prompted to review your automated estimate and order details before checkout.`,
    time: "Instant estimate",
  },
  {
    step: "03",
    phase: "Production",
    title: "Start Production",
    description:
      "Check out securely, confirm your measurements, and track the order.",
    detail:
      "Our team works with your dressmaker through production and delivery. Payment plans and buyer protection are available.",
    time: "Typically 2–3 months",
  },
];

interface HowItWorksSectionProps {
  title?: string;
  subtitle?: string;
  ctaText?: string;
  ctaHref?: string;
  className?: string;
}

export default function HowItWorksSection({
  title = "How It Works",
  subtitle,
  ctaText = "Start My Dress",
  ctaHref = "/create",
  className = "",
}: HowItWorksSectionProps) {
  return (
    <TrackedSection
      sectionId="how-it-works"
      sectionName="How It Works"
      className={`bg-background py-14 sm:py-20 ${className}`}
    >
      <div className="mx-auto max-w-6xl px-6 sm:px-8">
        <div className="mb-10 max-w-2xl sm:mb-12">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-brand-logo">
            From idea to delivery
          </p>
          <h2 className="text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            {title}
          </h2>
          {subtitle ? (
            <p className="mt-4 text-base leading-7 text-muted-foreground sm:text-lg">
              {subtitle}
            </p>
          ) : (
            <p className="mt-4 text-base leading-7 text-muted-foreground sm:text-lg">
              Create your design, review the estimate, and begin production when
              you are ready.
            </p>
          )}
        </div>

        <ol className="grid md:grid-cols-3 md:gap-5">
          {PROCESS_STEPS.map((step, index) => (
            <li
              key={step.step}
              className="relative flex gap-4 pb-9 last:pb-0 md:flex-col md:gap-0 md:rounded-2xl md:border md:border-border/80 md:border-t-2 md:border-t-brand-logo/60 md:bg-card md:p-6 md:shadow-sm md:last:pb-6"
            >
              {index < PROCESS_STEPS.length - 1 && (
                <span
                  aria-hidden="true"
                  className="absolute bottom-0 left-5 top-10 w-px bg-brand-logo/30 md:hidden"
                />
              )}
              <span
                aria-hidden="true"
                className="relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full border border-brand-logo/50 bg-brand-logo/10 text-sm font-bold text-brand-logo md:mb-7"
              >
                {step.step}
              </span>
              <div className="min-w-0 flex-1 pt-0.5 md:pt-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-logo">
                    {step.phase}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {step.time}
                  </span>
                </div>
                <h3 className="mt-2 text-xl font-semibold leading-tight tracking-tight text-foreground sm:text-2xl">
                  <span className="sr-only">Step {index + 1}: </span>
                  {step.title}
                </h3>
                <p className="mt-3 text-base leading-7 text-foreground/90">
                  {step.description}
                </p>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  {step.detail}
                </p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-10 sm:mt-12">
          <Button
            asChild
            variant="gradient"
            size="xl"
            className="w-full rounded-xl px-6 text-base sm:w-auto"
          >
            <Link href={ctaHref}>
              {ctaText}
              <ArrowRight aria-hidden="true" className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </TrackedSection>
  );
}
